export const validateForm = (values, required = []) => {
  const errors = {};
  let isValid = true;
  
  required.forEach(field => {
    if (values[field] === undefined || values[field] === null || values[field] === '') {
      errors[field] = `This field is required`;
      isValid = false;
    }
  });
  
  return { isValid, errors };
};

/**
 * Truncates text to a specified length with ellipsis
 * @param {string} text - The text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} Truncated text with ellipsis if needed
 */
export const truncateText = (text, maxLength = 100) => {
  if (!text || text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Formats a file size in bytes to a human-readable string
 * @param {number} bytes - Size in bytes
 * @returns {string} Formatted size with unit
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Generates a unique ID with an optional prefix
 * @param {string} prefix - Optional prefix for the ID
 * @returns {string} A unique ID
 */
export const generateId = (prefix = '') => {
  return `${prefix}${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Debounces a function to limit how often it can be called
 * @param {Function} func - The function to debounce
 * @param {number} wait - The time to wait in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait = 300) => {
  let timeout;
  
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Formats a date string into a readable format
 * @param {string} dateString - The date string to format
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Invalid date';
  
  return date.toLocaleString();
};

/**
 * Get authentication state from localStorage
 * @returns {Object} Auth state object
 */
export const getAuthState = () => {
  try {
    const authState = localStorage.getItem('authState');
    return authState ? JSON.parse(authState) : { isAuthenticated: false, user: null };
  } catch (error) {
    console.error('Failed to parse auth state from localStorage:', error);
    return { isAuthenticated: false, user: null };
  }
};

/**
 * Create a full API URL with environment
 * @param {string} endpoint - API endpoint
 * @returns {string} Full API URL
 */
export const createApiUrl = (endpoint) => {
  // Add leading slash if not present
  if (!endpoint.startsWith('/')) {
    endpoint = '/' + endpoint;
  }
  
  const baseUrl = 'https://3020h0l7mc.execute-api.us-west-2.amazonaws.com';
  
  return `${baseUrl}${endpoint}`;
};

/**
 * Handle API errors consistently
 * @param {Error} error - The error object
 * @returns {Error} Processed error object
 */
export const handleApiError = (error) => {
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    console.error('API Error Response:', error.response.data || error.response);
    
    // Check if the response contains a message that indicates success but in error format
    if (error.response.data?.success === true && error.response.data?.agents) {
      // This is a special case where we have a successful response inside an error
      console.log('Found successful data inside error response');
      return error.response.data;
    }
    
    // Special case for nested responseBody structure (seen in the error logs)
    if (error.response.responseBody && error.response.responseBody.success === true) {
      console.log('Found successful nested data in responseBody');
      return error.response.responseBody;
    }
    
    if (error.response.status === 401 || error.response.status === 403) {
      return new Error('Authentication error. Please log in again or check your permissions.');
    }
    
    return new Error(error.response.data?.message || error.response.statusText || 'Server error occurred');
  } else if (error.request) {
    // The request was made but no response was received
    console.error('API No Response:', error.request);
    
    // Check if we have direct testing results in the error message
    if (typeof error.message === 'string' && error.message.includes('results of the direct test')) {
      try {
        // Try to extract the JSON from the error message
        const match = error.message.match(/direct test to the api: ({.*})/);
        if (match && match[1]) {
          const directTestResults = JSON.parse(match[1]);
          
          // Check if the direct test actually has data
          if (directTestResults.responseBody && directTestResults.responseBody.success === true) {
            console.log('Found data in direct test results');
            return directTestResults.responseBody;
          }
        }
      } catch (parseError) {
        console.error('Failed to parse direct test results:', parseError);
      }
    }
    
    return new Error('No response from server. Please check your connection.');
  } else {
    // Something happened in setting up the request that triggered an Error
    console.error('API Request Error:', error.message);
    return error;
  }
};